/**
 * Where a tour step points, and what the screen shows there, read from the
 * marks screens put on the DOM (src/lib/tour.ts). Pure over a root element,
 * so it is tested on plain markup.
 */
import type { PageState, RegionState } from '@/lib/tour';
import type { TourStep } from './steps';

/** What the region under a step is showing, as the card explains it. */
export type TargetState = RegionState;

export type Resolved = {
  /** The highlighted element; null when none of the step's anchors is on screen. */
  element: HTMLElement | null;
  state: TargetState;
};

const visible = (element: Element): element is HTMLElement => {
  if (!(element instanceof HTMLElement)) return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const regionState = (element: HTMLElement): RegionState => {
  const own = element.getAttribute('data-tour-state') ?? element.querySelector('[data-tour-state]')?.getAttribute('data-tour-state');
  return own === 'loading' || own === 'error' || own === 'empty' ? own : 'content';
};

/** The innermost screen-level state: a screen-wide empty state sits inside a ready screen. */
const pageState = (root: ParentNode): PageState | null => {
  const marks = root.querySelectorAll('[data-tour-page]');
  const value = marks.length ? marks[marks.length - 1]?.getAttribute('data-tour-page') : null;
  return value === 'loading' || value === 'error' || value === 'empty' || value === 'ready' ? value : null;
};

/**
 * The first anchor on screen and its region's state. With no anchor on
 * screen, the screen says why: still loading, an error, or nothing to show.
 * A ready screen without the anchor (a region that does not apply) reads as
 * empty, so the card explains the region instead of pointing at nothing.
 */
export function resolveStep(step: TourStep, root: ParentNode = document): Resolved {
  for (const selector of step.anchors) {
    const element = [...root.querySelectorAll(selector)].find(visible);
    if (element) return { element, state: regionState(element) };
  }
  const page = pageState(root);
  return { element: null, state: page === 'loading' || page === 'error' ? page : page === null ? 'loading' : 'empty' };
}

/** The line a card adds for a region that is not showing its content. */
export const STATE_NOTES: Record<Exclude<TargetState, 'content'>, string> = {
  loading: 'This is still loading. The highlight moves here as soon as it is ready.',
  error: 'This couldn’t load right now; it shows here once it does.',
  empty: 'There is nothing to show here yet for this selection.',
};
